import React, { useCallback, useEffect, useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import type { BaseAutocompleteValue, BaseAutocompleteProps } from '../../types';
import {
  CHIP_MIN_WIDTH,
  CHIP_MAX_WIDTH,
  COUNT_CHIP_WIDTH,
  CHIP_GAP,
  INPUT_MIN_WIDTH,
  END_ADORNMENT_WIDTH,
} from '../utils/chipLayoutConstants';
import { AutocompleteChip, LimitCountChip } from './';
/**
 * Styled container for chips.
 * Uses flex: 0 1 auto so it only takes the space needed for its content.
 */
const ChipsContainer = styled(Stack)({
  flex: '0 1 auto',
  minWidth: 0,
  alignItems: 'center',
  flexWrap: 'nowrap',
  gap: CHIP_GAP,
});
/**
 * Inner container that holds the visible chips.
 */
const ChipsWrapper = styled(Box)({
  display: 'flex',
  flexWrap: 'nowrap',
  flex: '0 1 auto',
  minWidth: 0,
  gap: CHIP_GAP,
});
/**
 * Container for the count chip - never shrinks or gets clipped.
 */
const CountChipWrapper = styled(Box)({
  flexShrink: 0,
  marginLeft: CHIP_GAP,
});
export interface BaseAutocompleteChipsProps {
  /** Selected values to render as chips */
  values: BaseAutocompleteValue[];
  /** Function to get tag props from MUI Autocomplete */
  getTagProps: (params: { index: number }) => Record<string, unknown>;
  /** Translation function */
  t: (key: string, options?: Record<string, string | number>) => string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Function to map option to value format */
  mapOptionToValue: BaseAutocompleteProps['mapOptionToValue'];
  /** Callback to toggle the dropdown open state */
  onToggleOpen: () => void;
}
/**
 * Calculates how many chips can fit in the available space.
 *
 * Priority:
 * 1. Always show at least 1 chip when values exist
 * 2. Reserve space for count chip if totalChips \> 1
 * 3. Additional chips only shown if they fully fit at min-width
 *
 * @param availableWidth - Total available width in pixels for chips
 * @param totalChips - Total number of chips to display
 * @returns Object with visibleCount, whether to show count chip, and max width per chip
 */
const calculateVisibleChips = (availableWidth: number, totalChips: number) => {
  if (totalChips === 0) {
    return { visibleCount: 0, showCountChip: false, chipMaxWidth: CHIP_MAX_WIDTH };
  }
  // Fallback for initial render before measurement - show 1 chip
  if (availableWidth === 0) {
    return { visibleCount: 1, showCountChip: totalChips > 1, chipMaxWidth: CHIP_MAX_WIDTH };
  }
  const chipWithGap = CHIP_MIN_WIDTH + CHIP_GAP;
  // Case 1: Only one chip - always show it, can use all available space
  if (totalChips === 1) {
    const maxWidth = '100%';
    return { visibleCount: 1, showCountChip: false, chipMaxWidth: maxWidth };
  }
  // Case 2: Multiple chips - need to consider count chip
  // Space needed for count chip when there are hidden items
  const countChipSpace = COUNT_CHIP_WIDTH + CHIP_GAP;
  // Check if all chips fit without needing a count chip
  const allChipsWidth = totalChips * CHIP_MIN_WIDTH + (totalChips - 1) * CHIP_GAP;
  if (allChipsWidth <= availableWidth) {
    // Calculate how much space each chip can have (distribute evenly, up to max)
    const totalGaps = (totalChips - 1) * CHIP_GAP;
    const spacePerChip = (availableWidth - totalGaps) / totalChips;
    const chipMaxWidth = Math.min(CHIP_MAX_WIDTH, spacePerChip);
    return { visibleCount: totalChips, showCountChip: false, chipMaxWidth };
  }
  // Not all chips fit - we need a count chip
  // Available space for regular chips = total - count chip space
  const availableForChips = availableWidth - countChipSpace;
  // First chip is always shown
  let visibleCount = 1;
  let usedWidth = CHIP_MIN_WIDTH;
  // Try to fit additional chips
  while (visibleCount < totalChips) {
    const nextChipWidth = chipWithGap; // gap before chip + chip itself
    if (usedWidth + nextChipWidth <= availableForChips) {
      visibleCount++;
      usedWidth += nextChipWidth;
    } else {
      break;
    }
  }
  // Calculate max width per chip based on available space
  const hiddenCount = totalChips - visibleCount;
  const totalGaps = visibleCount > 1 ? (visibleCount - 1) * CHIP_GAP : 0;
  const spacePerChip = (availableForChips - totalGaps) / visibleCount;
  const chipMaxWidth = Math.min(CHIP_MAX_WIDTH, Math.max(CHIP_MIN_WIDTH, spacePerChip));
  return {
    visibleCount,
    showCountChip: hiddenCount > 0,
    chipMaxWidth,
  };
};
/**
 * Custom hook for measuring available width for chips.
 * Uses a callback ref to react when the element is attached to the DOM.
 */
const useAvailableWidth = () => {
  const [width, setWidth] = useState(0);
  const [element, setElement] = useState<HTMLElement | null>(null);
  // Callback ref - React calls this when the element is attached/detached
  const callbackRef = useCallback((node: HTMLElement | null) => {
    setElement(node);
  }, []);
  useEffect(() => {
    if (!element) return;
    // Find the MuiFilledInput-root parent container
    const inputRoot = element.closest('.MuiInputBase-root');
    if (!inputRoot) return;
    const calculateAvailableWidth = () => {
      const parentWidth = inputRoot.clientWidth;
      // Subtract: padding (24px), input min width, end adornment
      const available = parentWidth - INPUT_MIN_WIDTH - END_ADORNMENT_WIDTH - 24;
      setWidth(Math.max(0, available));
    };
    calculateAvailableWidth();
    // Use ResizeObserver for efficient width tracking
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(calculateAvailableWidth);
      observer.observe(inputRoot);
      return () => observer.disconnect();
    }
    // Fallback for environments without ResizeObserver
    window.addEventListener('resize', calculateAvailableWidth);
    return () => window.removeEventListener('resize', calculateAvailableWidth);
  }, [element]);
  return { width, callbackRef };
};
/**
 * BaseAutocompleteChips renders chips for multi-select autocomplete.
 *
 * Features:
 * - Always shows at least 1 chip when values exist
 * - Shows additional chips only if they fully fit (min-width: 100px)
 * - Displays "+N" indicator for hidden chips
 * - Returns null when no values (takes no space)
 */
export const BaseAutocompleteChips: React.FC<BaseAutocompleteChipsProps> = ({
  values,
  getTagProps,
  t,
  disabled,
  mapOptionToValue,
  onToggleOpen,
}) => {
  const { width: availableWidth, callbackRef } = useAvailableWidth();
  const { visibleCount, showCountChip, chipMaxWidth } = useMemo(
    () => calculateVisibleChips(availableWidth, values.length),
    [availableWidth, values.length],
  );
  // Don't render anything if no values - takes no space
  if (values.length === 0) {
    return null;
  }
  const visibleValues = values.slice(0, visibleCount);
  const hiddenCount = values.length - visibleCount;
  return (
    <ChipsContainer ref={callbackRef} direction="row" data-testid="chips-container">
      {visibleCount > 0 && (
        <ChipsWrapper>
          {visibleValues.map((value, index) => {
            const tagProps = getTagProps({ index });
            return (
              <Box key={index} sx={{ flexShrink: 0, maxWidth: chipMaxWidth }}>
                <AutocompleteChip index={index} value={mapOptionToValue(value)} itemProps={tagProps} />
              </Box>
            );
          })}
        </ChipsWrapper>
      )}

      {showCountChip && hiddenCount > 0 && (
        <CountChipWrapper>
          <LimitCountChip disabled={disabled} onClick={onToggleOpen} numberOfHiddenValues={hiddenCount} t={t} />
        </CountChipWrapper>
      )}
    </ChipsContainer>
  );
};
